import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  BreadcrumbComponent,
  Button,
  Caret,
  DropdownMenu,
  Subtitle,
  Title,
} from '../../components/shared';
import { IMusselLineDetail } from '../../entities/farms.entities';
import prevIcon from '../../images/prev-image.png';
import nextIcon from '../../images/next-image.png';
import useMenuHandler from '../../lib/useMenuHandler';
import { formatNumber } from '../../entities/util-functions';
import LineTagButton from '../../components/lines/LineTagButton';
import getBreadcrumbMenu from '../../lib/BreadcrumbMenu';
import {
  ICycleResource,
  IMusselHarvest,
  IMusselSeeding,
} from '../../entities/growing.entities';
import HarvestPredictButton from '../../components/lines/HarvestPredictButton';
import SeedingTrackModal from '../../components/track-modals/SeedingTrackModal';
import AddTrackStatusModal from '../../components/track-modals/AddTrackStatusModal';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  selectAllFarms,
  SelectIsEditable,
  SelectIsFarmable,
} from '../../store/extra/extra.selector';
import { defaultDateFormat, displayValueUnit } from '../../lib/common.helpers';
import { SeedSizeRange, StageLabelSpan } from '../../components/CommonViews';
import metaIcon from '../../images/history_icon.png';
import { selectAccount } from '../../store/auth/auth.selector';
import LineCropTypeModal from '../../components/lines/LineCropTypeModal';
import LineManageButtonsView from './LineManageButtonsView';

interface Props {
  lineData: IMusselLineDetail;
  loadLine: () => void;
  cyclePart: ICycleResource | null;
  isPrevEnabled: boolean;
  isNextEnabled: boolean;
  onClickPrevGroup: () => void;
  onClickNextGroup: () => void;
  showEditLineModal: () => void;
  onSeedingClick: () => void;
  onAssessmentClick: () => void;
  onCatchSpatClick: () => void;
  onHarvestClick: () => void;
  onMaintenanceClick: () => void;
  onFloatingManageClick: () => void;
  onExtraSeedClick: () => void;
  checkedHistoryView: number;
  onHistoryViewClick: () => void;
  onMetaEditClick: () => void;
  onWasteClick: () => void;
}

const FlupsyMusselLineDesktop = ({
  lineData,
  loadLine,
  cyclePart,
  onClickPrevGroup,
  onClickNextGroup,
  isPrevEnabled,
  isNextEnabled,
  showEditLineModal,
  onSeedingClick,
  onAssessmentClick,
  onCatchSpatClick,
  onHarvestClick,
  onMaintenanceClick,
  onFloatingManageClick,
  onExtraSeedClick,
  checkedHistoryView,
  onHistoryViewClick,
  onMetaEditClick,
  onWasteClick,
}: Props) => {
  const lang = useSelector(selectLang);
  const isEditable = SelectIsEditable(lineData.farm_id);
  const isFarmable = SelectIsFarmable(lineData.farm_id);
  const unitType = useSelector(selectAccount)?.display_unit;

  const { redirectToLine } = useMenuHandler();
  const curFarm = useSelector(selectAllFarms).find(
    x => x.id === lineData.farm_id,
  );
  const lines = curFarm?.lines ?? [];
  const mainSeed = cyclePart?.main_seed;
  const lastAssessment =
    cyclePart && cyclePart.assessments.length > 0
      ? cyclePart?.assessments.reduce((p, c) =>
          !p || p.assessment_date < c.assessment_date ? c : p,
        )
      : null;
  const lastHarvest =
    cyclePart && cyclePart.harvests.length > 0
      ? cyclePart?.harvests.reduce((p, c) =>
          p.complete_date > c.complete_date ? p : c,
        )
      : null;

  const musselSeedings =
    cyclePart?.crop_type === 'MUSSEL'
      ? (cyclePart.seedings as IMusselSeeding[])
      : [];
  const longlineLength = musselSeedings.reduce((p, c) => p + c.line_length, 0);
  const cur = lines.findIndex(t => t.id === lineData.id) ?? -1;
  const prev = (cur - 1 + (lines.length ?? 0)) % (lines.length ?? 0);
  const next = (cur + 1) % (lines.length ?? 0);

  const breadcrumbItems = getBreadcrumbMenu('FARM_LINE', lang, {
    FARM_ID: lineData.farm_id,
    LINE_ID: lineData.id,
    FARM_NAME: lineData?.farm_name,
    LINE_NAME: lineData?.line_name,
  }).slice(0, -1);

  const [showLineSpat, setShowLineSpat] = useState(false);
  const [visCropType, setVisCropType] = useState(false);
  const [trackSeedings, setTrackSeedings] = useState<number[]>();
  const [qrSeed, setQrSeed] = useState<{
    farmID: number;
    lineID: number;
    seedingID: number;
    harvests: IMusselHarvest[];
  }>();

  const { totalHarvested, totalSeeded, totalStorage } = useMemo(
    () => ({
      totalHarvested: lineData.growing_cycle?.total_harvested_amount ?? 0,
      totalSeeded: lineData.growing_cycle?.total_seeded_amount ?? 0,
      totalStorage: lineData.growing_cycle?.total_storage ?? 0,
    }),
    [lineData],
  );

  function getNewestDate() {
    const timestamps = [
      lastHarvest?.complete_date,
      lastAssessment?.assessment_date,
      mainSeed?.planned_date_seed,
    ].filter(date => date !== undefined && date !== null) as number[];
    if (timestamps.length === 0) {
      return undefined;
    }
    return Math.max(...timestamps);
  }

  const lastActivityDate = getNewestDate();

  return (
    <div className='farm-line-desktop-template'>
      <div className='pt-28 pb-28 d-flex justify-between align-center'>
        <div className='d-flex' style={{ alignItems: 'baseline' }}>
          <BreadcrumbComponent items={breadcrumbItems} />
          {lines && (
            <div className='ml-17'>
              <span
                className='prev-link'
                onClick={() => redirectToLine(lineData.farm_id, lines[prev].id)}
              >
                <img src={prevIcon} alt='prev' />
              </span>
              <span className='line-name'>
                {translate(lang, 'Line %s', lineData.line_name)}
              </span>
              <span
                className='next-link'
                onClick={() => redirectToLine(lineData.farm_id, lines[next].id)}
              >
                <img src={nextIcon} alt='next' />
              </span>
            </div>
          )}
        </div>
        <div className='d-flex justify-between align-center'>
          {lineData.growing_cycle?.taxonomy_groups &&
            lineData.growing_cycle.taxonomy_groups.length > 0 && (
              <Button
                color='green'
                size={1}
                width='middle'
                type='bordered'
                className='mr-8 mxw-270'
              >
                {`${translate(lang, 'Taxonomies')}: ${lineData.growing_cycle.taxonomy_groups.map(x => x.name).join(', ')}`}
              </Button>
            )}
          <div className='d-flex justify-between align-center white-card-small pt-3 pb-3'>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='mr-26'
              name='prev'
              onClick={onClickPrevGroup}
              disabled={!isPrevEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='left' />
            </Button>
            <Subtitle
              size={4}
              color='black'
              align='left'
              fontWeight={500}
              disabled={!mainSeed}
            >
              {mainSeed?.season_name ?? translate(lang, 'Empty')}
            </Subtitle>
            <Button
              color='blue'
              size={3}
              width='default'
              type='transparent'
              className='ml-26'
              name='next'
              onClick={onClickNextGroup}
              disabled={!isNextEnabled}
              onlyIconDisabled
            >
              <Caret color='#5A607F' direction='right' />
            </Button>
          </div>
          <Button
            color='blue'
            size={0}
            width='default'
            type='bordered'
            className='ml-8'
            iconOnly
            onClick={onMetaEditClick}
            disabled={!isEditable}
          >
            <img src={metaIcon} alt='meta-icon' style={{ width: 40 }} />
          </Button>
          <Button
            color='blue'
            size={1}
            width='middle'
            type='bordered'
            className='ml-16'
            onClick={onHarvestClick}
            disabled={!mainSeed?.is_growing || !isFarmable}
          >
            {translate(lang, 'Harvest Complete')}
          </Button>
          <Button
            color='red'
            size={1}
            width='middle'
            type='bordered'
            className='ml-16'
            onClick={onWasteClick}
            disabled={!mainSeed?.is_growing || !isFarmable}
          >
            {translate(lang, 'Stripped To Waste')}
          </Button>
        </div>
      </div>
      <div className='white-card pt-28 pr-16 pb-28 pl-24 mb-16'>
        <div className='d-flex justify-between'>
          <div>
            <Title size={5} color='black-3' align='default' fontWeight={500}>
              {translate(lang, 'Line %s', lineData.line_name)}
            </Title>
            <div style={{ marginTop: '7px' }}>
              <div
                className='--spat-count'
                onClick={e => setShowLineSpat(true)}
              >
                {translate(
                  lang,
                  'Last Spat Count: %s',
                  lineData.last_spat_count ?? 0,
                )}
              </div>
            </div>
            {!!lineData.growing_cycle?.last_harvest ? (
              <div style={{ marginTop: '20px' }}>
                <Link
                  to={`/harvest/${lineData.growing_cycle.last_harvest.id}`}
                  style={{
                    fontStyle: 'italic',
                    textDecorationLine: 'underline',
                  }}
                >
                  <Subtitle
                    size={5}
                    color='gray'
                    align='default'
                    fontWeight={400}
                  >
                    {translate(lang, '_line_partially_harvested')}
                  </Subtitle>
                </Link>
                <Subtitle
                  size={5}
                  color='#000'
                  align='default'
                  fontWeight={500}
                >
                  {translate(lang, 'Harvested %s dz so far', totalHarvested)}
                </Subtitle>
              </div>
            ) : (
              !!lineData.last_harvest && (
                <div style={{ marginTop: '10px' }}>
                  <Link
                    to={`/harvest/${lineData.last_harvest.id}`}
                    style={{
                      fontStyle: 'italic',
                      textDecorationLine: 'underline',
                    }}
                  >
                    <Subtitle
                      size={5}
                      color='gray'
                      align='default'
                      fontWeight={400}
                    >
                      {translate(lang, 'View last harvest')}
                    </Subtitle>
                  </Link>
                </div>
              )
            )}
            <div className='mt-17'>
              <LineTagButton lineData={lineData} loadLine={loadLine} />
            </div>
          </div>
          <div>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4'
            >
              {translate(lang, 'Date seeded')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {defaultDateFormat(mainSeed?.planned_date_seed, '-')}
            </Subtitle>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4 mt-28'
            >
              {translate(lang, 'Spat Size')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {lastAssessment ? (
                displayValueUnit(
                  lastAssessment.shell_size.avg,
                  unitType,
                  'size_mm',
                  '-',
                )
              ) : (
                <SeedSizeRange data={mainSeed} />
              )}
            </Subtitle>
          </div>
          <div>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4'
            >
              {translate(
                lang,
                mainSeed?.is_growing
                  ? 'Planned harvest date'
                  : 'Harvested date',
              )}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {defaultDateFormat(
                lastAssessment?.planned_date_harvest ??
                  mainSeed?.planned_date_harvest,
                '-',
              )}
            </Subtitle>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4 mt-28'
            >
              {translate(lang, 'Batch size')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {totalStorage !== 0 ? (
                <>
                  [{lineData.growing_cycle?.main_seed?.spat_storage?.id}]{' '}
                  {lineData.growing_cycle?.main_seed?.spat_storage?.source}-{' '}
                  {`${formatNumber((totalSeeded / totalStorage) * 100)}%`}
                </>
              ) : (
                '-'
              )}
            </Subtitle>
          </div>
          <div>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4'
            >
              {translate(lang, 'Stage')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {longlineLength > 0 &&
                musselSeedings.map((x, i) => (
                  <div key={i}>
                    {`${formatNumber(
                      (x.line_length / longlineLength) * 100,
                    )}% ${translate(lang, 'stage')} `}
                    <StageLabelSpan stage={x.stage} farmId={lineData.farm_id} />
                  </div>
                ))}
            </Subtitle>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4 mt-28'
            >
              {translate(lang, 'Batch left in Flupsy')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {totalSeeded !== 0 ? (
                <>
                  {`${formatNumber((totalHarvested / totalSeeded) * 100)}%`} [
                  {totalSeeded} - {totalHarvested}{' '}
                  {translate(lang, 'moved to farm')}]
                </>
              ) : (
                '-'
              )}
            </Subtitle>
          </div>
          <div>
            <Subtitle
              size={3}
              color='black'
              align='left'
              fontWeight={400}
              className='mb-4'
            >
              {translate(lang, 'Last checked')}
            </Subtitle>
            <Subtitle size={4} color='black' align='left' fontWeight={500}>
              {lastActivityDate !== undefined
                ? defaultDateFormat(lastActivityDate, '-')
                : '-'}
            </Subtitle>
          </div>
          {isEditable && (
            <div>
              <DropdownMenu
                data={{ ...lineData, isRedirectLine: true }}
                onEdit={showEditLineModal}
                column='isFarm'
                moreMenus={[
                  ...(mainSeed
                    ? [
                        {
                          label: translate(lang, 'View Scanned QR'),
                          onClick: () => setTrackSeedings([mainSeed.id]),
                        },
                        {
                          label: translate(lang, 'Add Scanned QR'),
                          onClick: () =>
                            setQrSeed({
                              farmID: lineData.farm_id,
                              lineID: lineData.id,
                              seedingID: mainSeed.id,
                              harvests: cyclePart.harvests as IMusselHarvest[],
                            }),
                        },
                      ]
                    : []),
                  ...(!lineData.growing_cycle
                    ? [
                        {
                          label: translate(lang, 'Change crop type'),
                          onClick: () => setVisCropType(true),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>
        {!!lineData.growing_cycle?.predicted_date_harvest && (
          <div style={{ textAlign: 'right' }}>
            <HarvestPredictButton lineData={lineData} />
          </div>
        )}
      </div>
      <LineManageButtonsView
        lineData={lineData}
        cyclePart={cyclePart}
        checkedHistoryView={checkedHistoryView}
        onHistoryViewClick={onHistoryViewClick}
        onFloatingManageClick={onFloatingManageClick}
        onSeedingClick={onSeedingClick}
        onAssessmentClick={onAssessmentClick}
        onCatchSpatClick={onCatchSpatClick}
        onMaintenanceClick={onMaintenanceClick}
        onExtraSeedClick={onExtraSeedClick}
        loadLine={loadLine}
        showLineSpat={showLineSpat}
        setShowLineSpat={setShowLineSpat}
      />
      {!!trackSeedings && (
        <SeedingTrackModal
          visible={true}
          seedingIDs={trackSeedings}
          onClose={() => setTrackSeedings(undefined)}
        />
      )}
      {!!qrSeed && (
        <AddTrackStatusModal
          visible={true}
          seedingID={qrSeed.seedingID}
          lineID={qrSeed.lineID}
          harvests={qrSeed.harvests}
          onClose={() => setQrSeed(undefined)}
        />
      )}
      {visCropType && (
        <LineCropTypeModal
          visible={true}
          lineData={lineData}
          onClose={() => setVisCropType(false)}
          loadLine={loadLine}
        />
      )}
    </div>
  );
};

export default FlupsyMusselLineDesktop;
